import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import usePageMeta from '../utils/usePageMeta';
import { getCookie } from '../utils/csrf';
import { useAuth } from '../components/AuthContext';

const ContactPage = () => {
    usePageMeta({
        title: 'Contact — DoritoStats',
        description: 'Send feedback or report an issue with DoritoStats.',
    });

    const { user } = useAuth();
    const location = useLocation();

    // Collect context: router state (from ProviderBetaBanner / league pages) + referrer URL
    const routerContext = location.state || {};
    const context = {
        ...(routerContext.provider    && { provider:     routerContext.provider }),
        ...(routerContext.league_id   && { league_id:    routerContext.league_id }),
        ...(routerContext.league_year && { league_year:  String(routerContext.league_year) }),
        ...(routerContext.league_name && { league_name:  routerContext.league_name }),
        page_url: window.location.href,
        ...(document.referrer && { referrer: document.referrer }),
    };

    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');

    // Pre-fill email from auth context when user is signed in
    useEffect(() => {
        if (user?.email) {
            setForm((prev) => ({ ...prev, email: prev.email || user.email }));
        }
    }, [user]);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMsg('');

        try {
            const csrftoken = getCookie('csrftoken');
            const response = await fetch('/api/contact/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrftoken,
                },
                credentials: 'include',
                body: JSON.stringify({ ...form, context }),
            });
            const data = await response.json();

            if (!response.ok) {
                setErrorMsg(data.error || 'Something went wrong. Please try again.');
                setStatus('error');
            } else {
                setStatus('success');
            }
        } catch {
            setErrorMsg('Something went wrong. Please try again.');
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen bg-bg flex flex-col">
            <Header />

            {/* Hero Band */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <div className="flex items-center gap-3">
                        <MessageCircle className="w-8 h-8 text-mustard flex-shrink-0" aria-hidden="true" />
                        <h1 className="font-display text-4xl font-black text-white leading-none">
                            Get in Touch
                        </h1>
                    </div>
                    <p className="mt-2 text-green-mid font-sans text-sm">
                        Have feedback, spotted a bug, or just want to say hi? We&rsquo;d love to hear from you.
                    </p>
                </div>
            </div>

            {/* Content */}
            <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-12">
                <div className="bg-surface border border-border-card rounded-card shadow-card p-8 max-w-xl">
                    {status === 'success' ? (
                        <div className="flex flex-col items-center gap-4 py-6 text-center">
                            <CheckCircle className="w-12 h-12 text-green" />
                            <h2 className="font-display text-2xl font-black text-ds-text">
                                Message sent!
                            </h2>
                            <p className="text-text-mid font-sans text-sm leading-relaxed">
                                Thanks for reaching out. We&rsquo;ll get back to you soon.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                            {/* Show league context that will be included automatically */}
                            {(context.league_name || context.league_id) && (
                                <div className="flex items-start gap-2 bg-green-light border border-green-mid/30 border-l-[3px] border-l-green-mid rounded-lg px-3 py-2.5 text-xs text-text-mid">
                                    <span className="font-semibold leading-relaxed">
                                        📎 Your message will include context about{' '}
                                        <span className="font-extrabold text-ds-text">
                                            {context.league_name || `League ${context.league_id}`}
                                            {context.league_year ? ` (${context.league_year})` : ''}
                                        </span>
                                        {context.provider ? ` on ${context.provider.charAt(0).toUpperCase() + context.provider.slice(1)}` : ''}.
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-col gap-1.5">
                                <label
                                    htmlFor="name"
                                    className="text-xs font-extrabold text-text-mid uppercase tracking-wide"
                                >
                                    Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    required
                                    maxLength={100}
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="Your name"
                                    className="w-full px-3 py-2 text-sm font-sans bg-bg border border-border rounded-lg text-ds-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-green focus:border-green transition"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label
                                    htmlFor="email"
                                    className="text-xs font-extrabold text-text-mid uppercase tracking-wide"
                                >
                                    Email
                                    {user?.email && (
                                        <span className="ml-2 text-[0.65rem] font-bold text-green normal-case tracking-normal">
                                            ✓ pre-filled from your account
                                        </span>
                                    )}
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    maxLength={200}
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="you@example.com"
                                    className="w-full px-3 py-2 text-sm font-sans bg-bg border border-border rounded-lg text-ds-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-green focus:border-green transition"
                                />
                            </div>

                            <div className="flex flex-col gap-1.5">
                                <label
                                    htmlFor="message"
                                    className="text-xs font-extrabold text-text-mid uppercase tracking-wide"
                                >
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    required
                                    maxLength={2000}
                                    rows={5}
                                    value={form.message}
                                    onChange={handleChange}
                                    placeholder="Tell us what's on your mind..."
                                    className="w-full px-3 py-2 text-sm font-sans bg-bg border border-border rounded-lg text-ds-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-green focus:border-green transition resize-none"
                                />
                            </div>

                            {status === 'error' && (
                                <p className="text-xs font-semibold text-coral leading-relaxed">
                                    {errorMsg}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="self-start px-5 py-2.5 bg-mustard text-forest font-sans font-extrabold text-sm rounded-lg shadow-mustard hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                            >
                                {status === 'submitting' ? 'Sending…' : 'Send Message'}
                            </button>
                        </form>
                    )}
                </div>
            </main>
        </div>
    );
};

export default ContactPage;
